import React, { useCallback, useEffect, useMemo, useState, useImperativeHandle, useRef } from 'react';
import ReactFlow, {
  Controls,
  Background,
  MarkerType,
  useNodesState,
  useEdgesState,
  useReactFlow,
  useStoreApi,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import QuestionNode from './QuestionNode';
import ResponseEdge from './ResponseEdge';
import ConfirmDialog from '../shared/ConfirmDialog';
import Modal from '../shared/Modal';
import Button from '../shared/Button';
import { promptDialog } from '../../utils/dialogHelper';
import NodeResponsesTable from '../shared/NodeResponsesTable';

// Suppress ResizeObserver errors - this is a known React Flow issue
const originalError = console.error;
console.error = (...args) => {
  if (args[0]?.includes?.('ResizeObserver loop completed with undelivered notifications') ||
        args[0]?.includes?.('ResizeObserver loop limit exceeded')) {
    return; // Suppress ResizeObserver errors
  }
  originalError(...args);
};

// Store original window.onerror for later use
const originalWindowError = window.onerror;

// Suppress ResizeObserver errors at window level
window.addEventListener('error', (e) => {
  if (e.message?.includes?.('ResizeObserver loop')) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});

// Handle unhandled errors (modern browsers)
window.addEventListener('error', (event) => {
  if (event.message?.includes?.('ResizeObserver loop completed with undelivered notifications') ||
        event.message?.includes?.('ResizeObserver loop limit exceeded') ||
        event.message?.includes?.('ResizeObserver loop completed') ||
        event.error?.message?.includes?.('ResizeObserver')) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }
});

// Handle unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason?.message?.includes?.('ResizeObserver loop completed with undelivered notifications') ||
        event.reason?.message?.includes?.('ResizeObserver loop limit exceeded') ||
        event.reason?.message?.includes?.('ResizeObserver loop completed') ||
        event.reason?.toString?.()?.includes?.('ResizeObserver')) {
    event.preventDefault();
    return;
  }
});

// Consolidated global error handler for ResizeObserver
window.onerror = (message, source, lineno, colno, error) => {
  if (typeof message === 'string' && (
    message.includes('ResizeObserver loop completed with undelivered notifications') ||
        message.includes('ResizeObserver loop limit exceeded') ||
        message.includes('ResizeObserver loop completed')
  )) {
    return true; // Suppress the error
  }
    
  if (originalWindowError) {
    return originalWindowError(message, source, lineno, colno, error);
  }
  return false;
};

const nodeTypes = { 
  question: QuestionNode
};
const edgeTypes = { response: ResponseEdge };


// Gate verbose logging behind debug flag (set window.FLOWCHART_DEBUG=true in console to enable)
const fcDebug = (...args) => {
  if (typeof window !== 'undefined' && window.FLOWCHART_DEBUG) {
    console.log(...args);
  }
};

// ============================================================================
// CONVERSION UTILITIES: Bridge between custom graph format and ReactFlow format
// ============================================================================

/**
 * Convert custom graph format to ReactFlow format
 * Input: { nodes: { id: {script, responses} }, ui: { positions: {id: {x,y}} } }
 * Output: { nodes: [{id, position, data, type}], edges: [{id, source, target}] }
 */
const graphToReactFlow = (graph, steps = []) => {
  if (!graph || !graph.nodes) {
    return { nodes: [], edges: [] };
  }

  const reactFlowNodes = [];
  const reactFlowEdges = [];
  const positions = graph.ui?.positions || {};
  
  // Create step name lookup map
  const stepNameMap = {};
  steps.forEach(step => {
    stepNameMap[step.id] = step.name || step.id;
  });

  // Convert nodes - Must include title, snippet, callbacks that QuestionNode expects
  // EXCLUDE objection nodes from rendering
  Object.entries(graph.nodes).forEach(([nodeId, nodeData]) => {
    // Skip objection nodes
    if (nodeId.startsWith('objection_') || nodeId.startsWith('objection_obj_')) {
      return;
    }
        
    let position = positions[nodeId] || { x: 0, y: 0 };
    
    // Look up step name for display
    const stepName = stepNameMap[nodeData.step] || nodeData.step;
        
    reactFlowNodes.push({
      id: nodeId,
      type: 'question',
      position,
      data: {
        ...nodeData, // Preserve original data (script, responses, step, etc.)
        id: nodeId,
        title: nodeId, // QuestionNode expects title
        snippet: typeof nodeData.script === 'string' ? nodeData.script.slice(0, 90) : 'Custom script', // QuestionNode expects snippet
        isOpeningNode: nodeData.isOpeningNode,
        stepName: stepName, // Human-readable step name for phase badge
        // Callbacks will be attached later after component mounts
        onEdit: null,
        onDelete: null
      }
    });
  });

  // Objection nodes are excluded from rendering (filtered out above), 
  // so no positioning logic needed for them
    
  // Auto-detect opening node if none is marked (backward compatibility for old/AI templates)
  const hasOpeningNode = reactFlowNodes.some(n => n.data.isOpeningNode);
  if (!hasOpeningNode) {
    // 1. Check graph.openingNode property (AI-generated scripts set this)
    let detected = false;
    if (graph.openingNode) {
      const openingNode = reactFlowNodes.find(n => n.id === graph.openingNode);
      if (openingNode) {
        openingNode.data.isOpeningNode = true;
        detected = true;
        fcDebug('🔧 Auto-detected opening node from graph.openingNode:', graph.openingNode);
      }
    }
    // 2. Fallback: check for nodeId === 'opening' (legacy templates)
    if (!detected) {
      const openingNode = reactFlowNodes.find(n => n.id === 'opening');
      if (openingNode) {
        openingNode.data.isOpeningNode = true;
        fcDebug('🔧 Auto-detected opening node for backward compatibility');
      }
    }
  }

  // Convert edges (responses → edges)
  // EXCLUDE edges that connect to/from objection nodes
  // Track parallel edges (same source→target) so each gets a different route
  const pairCount = {}; // "source→target" → count so far

  Object.entries(graph.nodes).forEach(([sourceId, nodeData]) => {
    if (sourceId.startsWith('objection_') || sourceId.startsWith('objection_obj_')) return;
    if (!nodeData.responses) return;

    Object.entries(nodeData.responses).forEach(([responseKey, response]) => {
      if (!response.nextNode) return;
      if (response.nextNode.startsWith('objection_') || response.nextNode.startsWith('objection_obj_')) return;
      const isEnd = response.nextNode === '__end_script__';
      const targetExists = !!graph.nodes[response.nextNode];
      if (!isEnd && !targetExists) return;

      const edgeId = `${sourceId}-${response.nextNode}-${responseKey}`;
      const pairKey = `${sourceId}→${response.nextNode}`;
      const idx = pairCount[pairKey] || 0;
      pairCount[pairKey] = idx + 1;

      const labelText = response.label || responseKey;

      reactFlowEdges.push({
        id: edgeId,
        source: sourceId,
        target: response.nextNode,
        type: 'response',
        data: {
          label: labelText,
          responseKey,
          parallelIndex: idx,
          responseType: response.type || 'predetermined',
        },
        sourceHandle: 'out-bottom',
        targetHandle: 'in-top',
        markerEnd: { type: MarkerType.ArrowClosed },
        style: { strokeWidth: 2, stroke: '#6b7280' },
        updatable: true,
        zIndex: 0,
      });
    });
  });

  // Always add end script node
  if (!reactFlowNodes.find(n => n.id === '__end_script__')) {
    reactFlowNodes.push({
      id: '__end_script__',
      type: 'question',
      position: positions['__end_script__'] || { x: 0, y: 800 },
      data: {
        script: 'End of Script',
        isEndNode: true, // This triggers the yellowish color in QuestionNode
        isEndScript: true, // Keep this for backward compatibility
        id: '__end_script__',
        title: '🏁 End Script',
        snippet: 'Call ends here. Thank the prospect and complete the lead form.',
        onEdit: undefined, // End script node cannot be edited
        onDelete: undefined // End script node cannot be deleted
      }
    });
  }
  fcDebug('📊 graphToReactFlow:', {
    inputNodes: Object.keys(graph.nodes).length,
    outputNodes: reactFlowNodes.length,
    outputEdges: reactFlowEdges.length
  });

  return { nodes: reactFlowNodes, edges: reactFlowEdges };
};

/**
 * Convert ReactFlow format back to custom graph format
 * Input: { nodes: [{id, position, data}], edges: [{source, target, data}] }
 * Output: { nodes: { id: {script, responses} }, ui: { positions: {id: {x,y}} } }
 */
const reactFlowToGraph = (reactFlowNodes, reactFlowEdges) => {
  const customGraph = {
    nodes: {},
    ui: { positions: {} }
  };

  // Preserve openingNode reference (needed for AI-generated scripts)
  const openingNodeRef = reactFlowNodes.find(n => n.data?.isOpeningNode);
  if (openingNodeRef) {
    customGraph.openingNode = openingNodeRef.id;
  }

  // Convert nodes
  reactFlowNodes.forEach(node => {
    // Skip phase group nodes - they're visual only and shouldn't be saved
    if (node.type === 'phaseGroup') {
      return;
    }
        
    if (node.id === '__end_script__') {
      // Store position for end script but don't add to nodes
      customGraph.ui.positions[node.id] = { ...node.position };
      return;
    }

    // Preserve ALL node data properties (not just specific ones)
    customGraph.nodes[node.id] = {
      script: node.data.script || '',
      responses: {}, // Will be populated from edges
      step: node.data.step || 'interest',
      // Preserve all other properties (coaching, isOpeningNode, etc.)
      ...(node.data.coaching && { coaching: node.data.coaching }),
      ...(node.data.isOpeningNode && { isOpeningNode: true }),
      ...(node.data.isEndScript && { isEndScript: true }),
      ...(node.data.isEndNode && { isEndNode: true })
    };

    customGraph.ui.positions[node.id] = { ...node.position };
  });

  // Convert edges to responses
  reactFlowEdges.forEach(edge => {
    const sourceNode = customGraph.nodes[edge.source];
    if (sourceNode) {
      const responseKey = edge.data?.responseKey || `response_${Object.keys(sourceNode.responses).length + 1}`;
      sourceNode.responses[responseKey] = {
        nextNode: edge.target,
        label: edge.data?.label || responseKey,
        type: edge.data?.responseType || 'predetermined',
        sourceHandle: edge.sourceHandle || 'out-bottom',
        targetHandle: edge.targetHandle || 'in-top'
      };
    }
  });

  fcDebug('📊 reactFlowToGraph:', {
    inputNodes: reactFlowNodes.length,
    inputEdges: reactFlowEdges.length,
    outputNodes: Object.keys(customGraph.nodes).length
  });

  return customGraph;
};

let elkInstance = null;

const layoutGraph = async (graph) => {
  if (!graph || !graph.nodes || Object.keys(graph.nodes).length === 0) {
    return { positions: {}, edgeRoutes: {} };
  }

  if (!elkInstance) {
    const ELK = (await import('elkjs/lib/elk.bundled.js')).default;
    elkInstance = new ELK();
  }

  const NODE_W = 350;
  const NODE_H = 260;

  const regularNodes = Object.keys(graph.nodes).filter(id =>
    !(id.startsWith('objection_obj_') || id.startsWith('objection_'))
  );
  if (regularNodes.length === 0) return { positions: {}, edgeRoutes: {} };

  const nodeSet = new Set(regularNodes);

  // Find the opening node for layer constraint
  const openingNodeId = regularNodes.find(id => {
    const n = graph.nodes[id];
    return n?.isOpeningNode || id === 'opening';
  }) || (graph.openingNode && nodeSet.has(graph.openingNode) ? graph.openingNode : null);

  // Build ELK nodes
  const elkChildren = regularNodes.map(id => {
    const nodeOpts = { id, width: NODE_W, height: NODE_H };
    if (id === openingNodeId) {
      nodeOpts.layoutOptions = { 'elk.layered.layerConstraint': 'FIRST' };
    }
    return nodeOpts;
  });

  // Add end script node if any response points to it
  const hasEndScriptTarget = regularNodes.some(id => {
    const n = graph.nodes[id];
    return n?.responses && Object.values(n.responses).some(r => r.nextNode === '__end_script__');
  });
  if (hasEndScriptTarget || graph.nodes['__end_script__']) {
    elkChildren.push({
      id: '__end_script__',
      width: NODE_W,
      height: 60,
      layoutOptions: { 'elk.layered.layerConstraint': 'LAST' }
    });
  }

  const allNodeIds = new Set(elkChildren.map(n => n.id));

  // Build ELK edges (deduplicated)
  const elkEdges = [];
  const addedEdges = new Set();
  regularNodes.forEach(srcId => {
    const n = graph.nodes[srcId];
    if (!n?.responses) return;
    Object.values(n.responses).forEach(r => {
      const tgt = r.nextNode;
      if (!tgt || !allNodeIds.has(tgt)) return;
      const edgeKey = `${srcId}->${tgt}`;
      if (addedEdges.has(edgeKey)) return;
      addedEdges.add(edgeKey);
      elkEdges.push({ id: edgeKey, sources: [srcId], targets: [tgt] });
    });
  });

  const elkGraph = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': 'DOWN',
      'elk.spacing.nodeNode': '50',
      'elk.layered.spacing.nodeNodeBetweenLayers': '80',
      'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
      'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
      'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
      'elk.edgeRouting': 'ORTHOGONAL',
      'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
      'elk.layered.mergeEdges': 'true',
    },
    children: elkChildren,
    edges: elkEdges,
  };

  const layoutResult = await elkInstance.layout(elkGraph);

  const positions = {};
  const nodeDims = {};
  layoutResult.children.forEach(node => {
    positions[node.id] = { x: node.x, y: node.y };
    nodeDims[node.id] = { w: node.width, h: node.height };
  });

  // Guarantee the opening node is the topmost node.
  // ELK's FIRST layer constraint is a hint, not a guarantee — enforce it explicitly.
  if (openingNodeId && positions[openingNodeId]) {
    const openingY = positions[openingNodeId].y;
    const minOtherY = Object.entries(positions)
      .filter(([id]) => id !== openingNodeId)
      .reduce((min, [, p]) => Math.min(min, p.y), Infinity);
    if (openingY > minOtherY) {
      // Opening node is below some other node — push all non-opening nodes down by the gap
      const shift = openingY - minOtherY + NODE_H + 80;
      Object.keys(positions).forEach(id => {
        if (id !== openingNodeId) positions[id].y += shift;
      });
    }
  }

  // Center the layout on origin
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  Object.entries(positions).forEach(([id, p]) => {
    const dim = nodeDims[id] || { w: NODE_W, h: NODE_H };
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x + dim.w);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y + dim.h);
  });
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  Object.keys(positions).forEach(id => {
    positions[id].x -= cx;
    positions[id].y -= cy;
  });

  fcDebug('📐 ELK layout complete:', {
    nodes: Object.keys(positions).length,
    edges: elkEdges.length,
    width: Math.round(maxX - minX),
    height: Math.round(maxY - minY)
  });

  return { positions, edgeRoutes: {} };
};

function EditorImpl({ graph, steps: templateSteps, onChange, onEditNode, onConfigureDetails, viewportPersistKey: viewportKeyFromProps, initialNodeId }, ref) {
    
  const { screenToFlowPosition, fitView, getNodes, getEdges, setViewport, getViewport, setCenter } = useReactFlow();
  const containerRef = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [editingEdgeId, setEditingEdgeId] = useState(null);
    
  // Track connection state manually since ReactFlow store clears it before onConnectEnd
  const [connectionState, setConnectionState] = useState(null);
  const [connectionSucceeded, setConnectionSucceeded] = useState(false);
    
  // Throttle rapid edge deletions to prevent crashes
  const deletionTimeoutRef = useRef(null);
  const pendingDeletionsRef = useRef([]);
    
  // Inline editing modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingNodeId, setEditingNodeId] = useState(null);
  // showAdvanced kept as always-true for unified modal
  const showAdvanced = true;
  const [editingNodeData, setEditingNodeData] = useState({
    id: '',
    script: '',
    coaching: '',
    step: 'interest'
  });
  const [originalEditingNodeData, setOriginalEditingNodeData] = useState(null); // Track original data for unsaved changes detection
  const [showDeleteNodeConfirm, setShowDeleteNodeConfirm] = useState(false);
  const [pendingDeleteNodeId, setPendingDeleteNodeId] = useState(null);
  const [showDiscardChangesConfirm, setShowDiscardChangesConfirm] = useState(false);
  const [showRenameConfirm, setShowRenameConfirm] = useState(false);
  const [pendingRenameResolve, setPendingRenameResolve] = useState(null);
  
  // Track whether current layout is from auto-layout (true) or manual positioning (false)
  const [isAutoLayout, setIsAutoLayout] = useState(false);
  const isAutoLayoutRef = useRef(false);
  const [isLayoutRunning, setIsLayoutRunning] = useState(false);

  // Help modal state
  const [showHelpModal, setShowHelpModal] = useState(() => {
    // Show on first visit, remember if dismissed
    return localStorage.getItem('flowchartHelpDismissed') !== 'true';
  });

  // Centralized logging for Flowchart Editor actions
  const logFlowchartAction = (action, details = {}) => {
    if (window.logCriticalAction) {
      window.logCriticalAction('ACTION', `Flowchart Editor: ${action}`, {
        ...details,
        nodeCount: nodes.length,
        edgeCount: edges.length,
        timestamp: new Date().toISOString()
      });
    } else {
      console.log(`🔧 Flowchart Editor: ${action}`, details);
    }
  };
    
  // ============================================================================
  // HANDLER DEFINITIONS: Must be defined BEFORE useEffect that references them
  // ============================================================================
    
  // ============================================================================
  // HELPER: Notify parent of changes by converting ReactFlow state → custom format
  // ============================================================================
  const notifyGraphChangeTimeoutRef = useRef(null);
    
  const notifyGraphChange = useCallback((updatedNodes, updatedEdges, immediate = false) => {
    fcDebug('🔄 notifyGraphChange called:', { immediate });
        
    // Clear any pending notification
    if (notifyGraphChangeTimeoutRef.current) {
      clearTimeout(notifyGraphChangeTimeoutRef.current);
    }
        
    const customGraph = reactFlowToGraph(updatedNodes || nodes, updatedEdges || edges);

    // Persist the manual/auto layout flag so it survives save/reload
    if (customGraph.ui) {
      customGraph.ui.isManualLayout = !isAutoLayoutRef.current;
    }
        
    fcDebug('📊 notifyGraphChange - converted graph:', {
      nodeCount: Object.keys(customGraph.nodes).length,
      hasPositions: !!customGraph.ui?.positions
    });
        
    if (immediate) {
      // Immediate for critical operations (delete, explicit save)
      onChange(customGraph);
    } else {
      // Debounce all other changes to reduce re-renders
      notifyGraphChangeTimeoutRef.current = setTimeout(() => {
        onChange(customGraph);
      }, 500);
    }
  }, [nodes, edges, onChange]);

  // Handler for opening inline edit modal
  const handleInlineEdit = useCallback((nodeId) => {
    fcDebug('🔧 handleInlineEdit called for nodeId:', nodeId);
        
    logFlowchartAction('Node edit modal opened', {
      nodeId: nodeId,
      nodeType: nodeId === '__end_script__' ? 'end_script' : 'regular'
    });

    if (nodeId === '__end_script__') {
      // Special handling for end script node
      const endNodeData = {
        id: nodeId,
        script: graph.endScriptConfig?.script || 'Thank you for your time. I\'ll now collect some basic information to better assist you.',
        coaching: graph.endScriptConfig?.coaching || 'Keep it brief and professional. Transition smoothly to data collection.',
        step: 'closing', // End scripts are typically closing phase
        isEndNode: true
      };
      setEditingNodeId(nodeId);
      setEditingNodeData(endNodeData);
      setOriginalEditingNodeData({ ...endNodeData }); // Store original for unsaved changes detection
      setShowEditModal(true);
      return;
    }
        
    // ALWAYS read from ReactFlow state first - it's the source of truth during editing
    // The graph prop might be stale due to debouncing
    const currentNodes = getNodes();
    const reactFlowNode = currentNodes.find(n => n.id === nodeId);
    fcDebug('🔧 ReactFlow node found:', reactFlowNode?.id);
    
    let nodeData;
    if (reactFlowNode) {
      // Prefer ReactFlow state - it has the latest edits
      fcDebug('🔧 ReactFlow node.data id:', reactFlowNode.data?.id);
      nodeData = {
        script: reactFlowNode.data?.script || '',
        coaching: reactFlowNode.data?.coaching || '',
        step: reactFlowNode.data?.step || 'interest',
        isOpeningNode: reactFlowNode.data?.isOpeningNode || false
      };
      fcDebug('🔧 Using nodeData from ReactFlow node');
    } else {
      // Fallback to graph prop for nodes not yet in ReactFlow state
      nodeData = graph?.nodes?.[nodeId];
      fcDebug('🔧 Fallback to graph prop');
    }
        
    if (!nodeData) {
      console.warn('⚠️ Node data not found for:', nodeId);
      return;
    }
        
    fcDebug('🔧 Opening edit modal with data:', {
      id: nodeId,
      script: nodeData.script,
      coaching: nodeData.coaching,
      step: nodeData.step,
      isOpeningNode: nodeData.isOpeningNode
    });
        
    const nodeEditData = {
      id: nodeId,
      script: nodeData.script || '',
      coaching: nodeData.coaching || '',
      step: nodeData.step || 'interest',
      isOpeningNode: nodeData.isOpeningNode || false
    };
    setEditingNodeId(nodeId);
    setEditingNodeData(nodeEditData);
    setOriginalEditingNodeData({ ...nodeEditData }); // Store original for unsaved changes detection
    setShowEditModal(true);
  }, [graph, getNodes]);
    
  // Handler for deleting nodes - opens confirmation dialog
  const handleDeleteNode = useCallback((nodeId) => {
    setPendingDeleteNodeId(nodeId);
    setShowDeleteNodeConfirm(true);
  }, []);

  // Actual delete logic, called after confirmation
  const confirmDeleteNode = useCallback(() => {
    const nodeId = pendingDeleteNodeId;
    if (!nodeId) return;
    setShowDeleteNodeConfirm(false);
    setPendingDeleteNodeId(null);
        
    fcDebug('🗑️ Deleting node:', nodeId);
        
    // Get current state before deletion
    const currentNodes = getNodes();
    const currentEdges = getEdges();
        
    // Calculate updated arrays
    const updatedNodes = currentNodes.filter(n => n.id !== nodeId);
    const updatedEdges = currentEdges.filter(e => e.source !== nodeId && e.target !== nodeId);
        
    // Node deleted — layout is no longer purely auto
    if (isAutoLayoutRef.current) {
      setIsAutoLayout(false);
      isAutoLayoutRef.current = false;
    }

    // Update ReactFlow state
    setNodes(updatedNodes);
    setEdges(updatedEdges);
        
    // Sync to parent with UPDATED arrays (not null!)
    notifyGraphChange(updatedNodes, updatedEdges, false);
        
    fcDebug('✅ Delete complete');
  }, [pendingDeleteNodeId, getNodes, getEdges, setNodes, setEdges, notifyGraphChange]);
    
  // ============================================================================
  // CORE STATE SYNC: Convert graph to ReactFlow format ONCE on mount only
  // ============================================================================
  // 
  // ARCHITECTURE: React unidirectional data flow
  // 
  // 1. ON MOUNT: graph (prop) → convert → ReactFlow state (useState)
  // 2. DURING EDITING: ReactFlow state is source of truth, ignore graph prop changes
  // 3. ON SAVE: ReactFlow state → convert → call onChange(customGraph)
  // 
  // This prevents the re-render loop where:
  // - User edits → onChange → parent updates → graph prop changes → re-convert → loses edits
  // 
  // ============================================================================
  const hasInitialized = useRef(false);
  const isEditingSession = useRef(false); // Track if we're in an active editing session
    
  // ============================================================================
  // VIEWPORT CENTERING: Handle automatic and manual viewport centering
  // ============================================================================
    
  // Center once after nodes load; restore last viewport if available
  const didAutoCenterRef = useRef(false);
  const viewportPersistKey = viewportKeyFromProps || 'flowViewportCurrent';
  const handleReactFlowInit = useCallback((_reactFlowInstance) => {
    // Defer to nodes-based effect to center/restore reliably
    fcDebug('🎯 ReactFlow initialized');
  }, []);
    
  // Manual center view handler for the button
  const handleCenterView = useCallback(() => {
    fcDebug('🎯 Manual center view triggered');
    const allNodes = getNodes();
    const openingNode = allNodes.find(n => n.data?.isOpeningNode || n.id === 'opening' || n.id === 'newNode_1');
        
    if (openingNode && openingNode.position) {
      const centerX = openingNode.position.x + 175;
      const centerY = openingNode.position.y + 105;
      setCenter(centerX, centerY, { zoom: 0.6, duration: 500 });
    } else {
      fitView({ padding: 0.1, duration: 500 });
    }
  }, [getNodes, setCenter, fitView]);

  // Persist viewport on pan/zoom so edits can restore position
  const persistViewport = useCallback((vpOverride) => {
    try {
      const vp = vpOverride || getViewport?.();
      if (vp && typeof vp.x === 'number') {
        sessionStorage.setItem(viewportPersistKey, JSON.stringify(vp));
      }
    } catch (_) { /* ignore storage errors */ }
  }, [getViewport, viewportPersistKey]);

  // Center or restore once when nodes are available
  useEffect(() => {
    if (didAutoCenterRef.current) return;
    if (!nodes || nodes.length === 0) return;
    try {
      const saved = sessionStorage.getItem(viewportPersistKey);
      if (saved) {
        const vp = JSON.parse(saved);
        if (vp && typeof vp.x === 'number' && typeof vp.y === 'number' && typeof vp.zoom === 'number') {
          setViewport({ x: vp.x, y: vp.y, zoom: vp.zoom });
          didAutoCenterRef.current = true;
          // Persist immediately to refresh timestamp
          setTimeout(() => persistViewport(vp), 100);
          return;
        }
      }
    } catch (_) { /* ignore storage errors */ }
    // No saved viewport: fit entire graph into view
    fitView({ padding: 0.15, duration: 500 });
    setTimeout(() => persistViewport(), 600);
    didAutoCenterRef.current = true;
  }, [nodes, setViewport, setCenter, fitView, persistViewport, viewportPersistKey]);
  
  // Focus on initial node when provided (from "Back to Flowchart" button in ScriptBuilder)
  const didFocusInitialNode = useRef(false);
  const lastInitialNodeId = useRef(null);
  
  // Reset focus flag when initialNodeId changes
  useEffect(() => {
    if (initialNodeId !== lastInitialNodeId.current) {
      didFocusInitialNode.current = false;
      lastInitialNodeId.current = initialNodeId;
    }
  }, [initialNodeId]);
  
  useEffect(() => {
    if (!initialNodeId || didFocusInitialNode.current || nodes.length === 0) return;
    
    const targetNode = nodes.find(n => n.id === initialNodeId);
    if (targetNode && targetNode.position) {
      fcDebug('🎯 Focusing on initial node:', initialNodeId);
      // Center on the node with a brief delay to ensure ReactFlow is ready
      setTimeout(() => {
        const centerX = targetNode.position.x + 175;
        const centerY = targetNode.position.y + 105;
        setCenter(centerX, centerY, { zoom: 0.8, duration: 500 });
        
        // Also open the edit modal for that node
        setTimeout(() => {
          handleInlineEdit(initialNodeId);
        }, 600);
      }, 100);
      didFocusInitialNode.current = true;
    }
  }, [initialNodeId, nodes, setCenter, handleInlineEdit]);
    
  useEffect(() => {
    // Only initialize once on mount, then ReactFlow state becomes source of truth
    if (hasInitialized.current) {
      return;
    }

    const initializeGraph = async () => {
        
      const { nodes: reactFlowNodes, edges: reactFlowEdges } = graphToReactFlow(graph, templateSteps);
        
      fcDebug('✅ Converted to ReactFlow nodes:', reactFlowNodes.length);

      // Determine if we should auto-layout:
      // - No positions at all → auto-layout (new template or never positioned)
      // - Has positions AND isManualLayout === true → keep manual positions
      // - Has positions AND isManualLayout === false → auto-layout (was auto before)
      // - Has positions AND isManualLayout undefined (legacy) → keep positions (backward compat)
      const hasPositions = graph?.ui?.positions && Object.keys(graph.ui.positions).length > 0;
      const explicitlyAutoLayout = graph?.ui?.isManualLayout === false;
      const shouldAutoLayout = !hasPositions || explicitlyAutoLayout;
      const hasNodes = Object.keys(graph?.nodes || {}).length > 0;

      let finalNodes;

      if (shouldAutoLayout && hasNodes) {
        fcDebug('🔄 Running auto-layout on initialization (no manual positions)');
        try {
          const { positions } = await layoutGraph(graph);
          
          if (Object.keys(positions).length > 0) {
            finalNodes = reactFlowNodes.map(node => ({
              ...node,
              position: positions[node.id] || node.position,
              data: {
                ...node.data,
                onEdit: () => handleInlineEdit(node.id),
                onDelete: node.id !== '__end_script__' ? () => handleDeleteNode(node.id) : undefined
              }
            }));
            setIsAutoLayout(true);
            isAutoLayoutRef.current = true;
            fcDebug('✅ Auto-layout applied on initialization');
          } else {
            // Layout failed, fall back to default
            finalNodes = reactFlowNodes.map(node => ({
              ...node,
              data: {
                ...node.data,
                onEdit: () => handleInlineEdit(node.id),
                onDelete: node.id !== '__end_script__' ? () => handleDeleteNode(node.id) : undefined
              }
            }));
            setIsAutoLayout(false);
            isAutoLayoutRef.current = false;
          }
        } catch (err) {
          console.error('🔄 Auto-layout on init failed:', err);
          finalNodes = reactFlowNodes.map(node => ({
            ...node,
            data: {
              ...node.data,
              onEdit: () => handleInlineEdit(node.id),
              onDelete: node.id !== '__end_script__' ? () => handleDeleteNode(node.id) : undefined
            }
          }));
          setIsAutoLayout(false);
          isAutoLayoutRef.current = false;
        }
      } else {
        // Use saved manual positions
        fcDebug('🔄 Using saved manual positions');
        finalNodes = reactFlowNodes.map(node => ({
          ...node,
          data: {
            ...node.data,
            onEdit: () => handleInlineEdit(node.id),
            onDelete: node.id !== '__end_script__' ? () => handleDeleteNode(node.id) : undefined
          }
        }));
        setIsAutoLayout(false);
        isAutoLayoutRef.current = false;
      }
        
      setNodes(finalNodes);
      setEdges(reactFlowEdges);
        
      // Mark as initialized - viewport centering will be handled by onInit callback
      hasInitialized.current = true;
      isEditingSession.current = true; // Start editing session
        
      fcDebug('✅ FlowChart initialized - ReactFlow state is now source of truth');
    };

    initializeGraph();
  }, [handleInlineEdit, handleDeleteNode]); // Dependencies for callback attachment

  // Handler for saving changes from inline edit modal
  const handleSaveInlineEdit = useCallback(async () => {
    if (!editingNodeId) return;
        
    // Special handling for end script node
    if (editingNodeId === 'END_SCRIPT') {
      const newGraph = JSON.parse(JSON.stringify(graph));
      if (!newGraph.endScriptConfig) {
        newGraph.endScriptConfig = {};
      }
      newGraph.endScriptConfig.script = editingNodeData.script;
      newGraph.endScriptConfig.coaching = editingNodeData.coaching;
      onChange(newGraph);
      setShowEditModal(false);
      setEditingNodeId(null);
      setOriginalEditingNodeData(null);
      return;
    }
        
    // If the node ID is being changed, warn the user and record an alias for analytics continuity
    if (editingNodeId !== editingNodeData.id) {
      const oldId = editingNodeId;
      const newId = editingNodeData.id;

      const proceed = await new Promise((resolve) => {
        setPendingRenameResolve(() => resolve);
        setShowRenameConfirm(true);
      });
      if (!proceed) return;
      
      // Update ReactFlow nodes - change the node ID
      setNodes((currentNodes) => {
        return currentNodes.map((node) => {
          if (node.id === oldId) {
            // This is the renamed node
            return {
              ...node,
              id: newId,
              data: {
                ...node.data,
                id: newId,
                title: newId,
                script: editingNodeData.script,
                coaching: editingNodeData.coaching,
                step: editingNodeData.step || node.data.step || 'interest',
                snippet: (editingNodeData.script || '').substring(0, 80),
                onEdit: () => handleInlineEdit(newId),
                onDelete: newId !== '__end_script__' ? () => handleDeleteNode(newId) : undefined
              }
            };
          }
          return node;
        });
      });
      
      // Update ReactFlow edges - update any references to the old ID
      setEdges((currentEdges) => {
        return currentEdges.map((edge) => {
          let updatedEdge = { ...edge };
          if (edge.source === oldId) {
            updatedEdge = { ...updatedEdge, source: newId, id: edge.id.replace(oldId, newId) };
          }
          if (edge.target === oldId) {
            updatedEdge = { ...updatedEdge, target: newId, id: edge.id.replace(oldId, newId) };
          }
          return updatedEdge;
        });
      });
      
      // Also update the parent graph
      const newGraph = JSON.parse(JSON.stringify(graph));
      newGraph.nodes[newId] = {
        ...newGraph.nodes[oldId],
        script: editingNodeData.script,
        coaching: editingNodeData.coaching,
        step: editingNodeData.step
      };
      if (newGraph.ui?.positions?.[oldId]) {
        if (!newGraph.ui.positions) newGraph.ui.positions = {};
        newGraph.ui.positions[newId] = newGraph.ui.positions[oldId];
        delete newGraph.ui.positions[oldId];
      }
      Object.values(newGraph.nodes).forEach(node => {
        if (node.responses) {
          Object.entries(node.responses).forEach(([key, resp]) => {
            if (resp.nextNode === oldId) {
              node.responses[key] = { ...resp, nextNode: newId };
            }
          });
        }
      });
      delete newGraph.nodes[oldId];

      // Record alias so analytics from before this rename continue to resolve
      const existingAliases = newGraph.nodeAliases || {};
      const updatedAliases = Object.fromEntries(
        Object.entries(existingAliases).map(([k, v]) => [k, v === oldId ? newId : v])
      );
      updatedAliases[oldId] = newId;
      newGraph.nodeAliases = updatedAliases;

      onChange(newGraph);
      
      setShowEditModal(false);
      setEditingNodeId(null);
      setOriginalEditingNodeData(null);
      return;
    }
        
    // Update ReactFlow nodes immediately for content edits (no ID change)
    setNodes((currentNodes) => {
      const updatedNodes = currentNodes.map((node) => {
        if (node.id !== editingNodeId) {
          // If this node was the opening node but we're setting another node as opening, unmark it
          if (editingNodeData.isOpeningNode && node.data.isOpeningNode) {
            return {
              ...node,
              data: {
                ...node.data,
                isOpeningNode: false
              }
            };
          }
          return node;
        }
        const newTitle = editingNodeData.id || node.data.title || node.id;
        const newSnippet = (editingNodeData.script || '').substring(0, 80) || node.data.snippet;
        // Look up step name for display
        const stepName = templateSteps?.find(s => s.id === editingNodeData.step)?.name || editingNodeData.step;
        return {
          ...node,
          data: {
            ...node.data,
            script: editingNodeData.script,
            coaching: editingNodeData.coaching,
            step: editingNodeData.step || node.data.step || 'interest',
            stepName: stepName,
            isOpeningNode: editingNodeData.isOpeningNode || false,
            title: newTitle,
            snippet: newSnippet
          }
        };
      });
      return updatedNodes;
    });
    
    // Sync to parent with latest edges (includes any response edits from the modal)
    setTimeout(() => {
      notifyGraphChange(getNodes(), getEdges(), true);
    }, 0);
        
    setShowEditModal(false);
    setEditingNodeId(null);
    setEditingNodeData({ id: '', script: '', coaching: '', step: 'interest' });
    setOriginalEditingNodeData(null);
  }, [editingNodeId, editingNodeData, graph, setNodes, setEdges, edges, notifyGraphChange, onChange, handleInlineEdit, handleDeleteNode, getNodes, getEdges]);

  // Check if there are unsaved changes in the edit modal
  const hasUnsavedModalChanges = useCallback(() => {
    if (!originalEditingNodeData) return false;
    return (
      editingNodeData.id !== originalEditingNodeData.id ||
      editingNodeData.script !== originalEditingNodeData.script ||
      editingNodeData.coaching !== originalEditingNodeData.coaching ||
      editingNodeData.step !== originalEditingNodeData.step ||
      editingNodeData.isOpeningNode !== originalEditingNodeData.isOpeningNode
    );
  }, [editingNodeData, originalEditingNodeData]);

  // Handler for canceling inline edit (with unsaved changes check)
  const handleCancelInlineEdit = useCallback(() => {
    if (hasUnsavedModalChanges()) {
      setShowDiscardChangesConfirm(true);
      return;
    }
    setShowEditModal(false);
    setEditingNodeId(null);
    setEditingNodeData({
      id: '',
      script: '',
      coaching: '',
      step: 'interest'
    });
    setOriginalEditingNodeData(null);
  }, [hasUnsavedModalChanges]);

  // Confirmed discard of unsaved changes in edit modal
  const confirmDiscardChanges = useCallback(() => {
    setShowDiscardChangesConfirm(false);
    setShowEditModal(false);
    setEditingNodeId(null);
    setEditingNodeData({
      id: '',
      script: '',
      coaching: '',
      step: 'interest'
    });
    setOriginalEditingNodeData(null);
  }, []);

  // Expose imperative API to parent: getCurrentGraph returns live ReactFlow state
  useImperativeHandle(ref, () => ({
    getCurrentGraph: () => {
      const liveNodes = getNodes();
      const liveEdges = getEdges();
      return reactFlowToGraph(liveNodes, liveEdges);
    }
  }), [getNodes, getEdges]);
    
  // ============================================================================
  // EVENT HANDLERS: Work with ReactFlow state directly
  // ============================================================================

  // Throttled edge deletion to prevent crashes from rapid deletions
  const handleEdgesDelete = useCallback((edgesToDelete) => {
    try {
      fcDebug('🗑️ Queuing edges for deletion:', edgesToDelete.length);
            
      if (!graph || !graph.nodes) {
        console.error('🗑️ Invalid graph state during deletion:', graph);
        return;
      }
            
      // Add to pending deletions
      pendingDeletionsRef.current = [...pendingDeletionsRef.current, ...edgesToDelete];
            
      // Clear existing timeout
      if (deletionTimeoutRef.current) {
        clearTimeout(deletionTimeoutRef.current);
      }
            
      // Process all pending deletions after a short delay
      deletionTimeoutRef.current = setTimeout(() => {
        try {
          const allDeletions = [...pendingDeletionsRef.current];
          pendingDeletionsRef.current = []; // Clear pending
                    
          fcDebug('🗑️ Processing batched deletions:', allDeletions.length);
                    
          const newGraph = JSON.parse(JSON.stringify(graph));
          let hasChanges = false;
                    
          allDeletions.forEach(edge => {
            if (!edge || !edge.id) {
              console.warn('🗑️ Invalid edge during deletion:', edge);
              return;
            }
                        
            // Use edge data for reliable source/responseKey resolution
            const sourceId = edge.source || edge.id.split('-')[0];
            const responseKey = edge.data?.responseKey;
                        
            if (!sourceId) {
              console.warn('🗑️ Invalid edge ID format:', edge.id);
              return;
            }
                        
            if (responseKey && newGraph.nodes[sourceId]?.responses?.[responseKey]) {
              fcDebug('🗑️ Removing response:', sourceId, responseKey);
              delete newGraph.nodes[sourceId].responses[responseKey];
              hasChanges = true;
            }
          });
                    
          if (hasChanges) {
            fcDebug('🗑️ Applying batched deletion changes');
            // Apply structural changes
            onChange(newGraph);
          }
        } catch (error) {
          console.error('🗑️ Error in batched deletion processing:', error);
          // Reset pending deletions on error
          pendingDeletionsRef.current = [];
        }
      }, 100); // 100ms batch delay
            
    } catch (error) {
      console.error('🗑️ Error in handleEdgesDelete:', error);
    }
  }, [graph, onChange]);

  // Custom edges change handler that intercepts deletions
  const handleEdgesChange = useCallback((changes) => {
    try {
      // Find any remove changes (deletions)
      const deletions = changes.filter(change => change.type === 'remove');
            
      if (deletions.length > 0) {
        fcDebug('🗑️ Edge changes detected:', deletions.length);
                
        // Check if any nodes are currently selected
        const selectedNodes = nodes.filter(node => node.selected);
                
        if (selectedNodes.length > 0) {
          fcDebug('🗑️ Node(s) selected - preventing edge deletion');
          // Don't process edge deletions when nodes are selected
          // This prevents accidental deletion of all connected edges when user selects a node and presses delete
                    
          // Filter out deletion changes to prevent visual removal
          const nonDeletionChanges = changes.filter(change => change.type !== 'remove');
          onEdgesChange(nonDeletionChanges);
          return;
        }
                
        // Get the full edge objects for deletion
        const edgesToDelete = deletions.map(deletion => 
          edges.find(edge => edge.id === deletion.id)
        ).filter(Boolean);
                
        if (edgesToDelete.length > 0) {
          handleEdgesDelete(edgesToDelete);
        }
      }
            
      // Apply all changes to ReactFlow's edge state
      onEdgesChange(changes);
    } catch (error) {
      console.error('🗑️ Error in handleEdgesChange:', error);
      // Still try to apply the changes to prevent complete failure
      try {
        onEdgesChange(changes);
      } catch (fallbackError) {
        console.error('🗑️ Fallback edge change also failed:', fallbackError);
      }
    }
  }, [edges, nodes, handleEdgesDelete, onEdgesChange]);

  // Custom nodes change handler that prevents accidental node deletion
  const handleNodesChange = useCallback((changes) => {
    try {
      // Find any remove changes (deletions)
      const deletions = changes.filter(change => change.type === 'remove');
            
      if (deletions.length > 0) {
        fcDebug('🗑️ Node deletion attempted - preventing');
        // Filter out deletion changes to prevent any node deletion via delete key
        const nonDeletionChanges = changes.filter(change => change.type !== 'remove');
        onNodesChange(nonDeletionChanges);
        return;
      }
            
      // Apply all non-deletion changes to ReactFlow's node state
      onNodesChange(changes);
    } catch (error) {
      console.error('🗑️ Error in handleNodesChange:', error);
      // Still try to apply the changes to prevent complete failure
      try {
        onNodesChange(changes);
      } catch (fallbackError) {
        console.error('🗑️ Fallback node change also failed:', fallbackError);
      }
    }
  }, [onNodesChange]);
    
  const onConnectStart = useCallback((_, { nodeId, handleId, handleType }) => {
    setConnectionState({
      source: nodeId,
      sourceHandle: handleId,
      handleType
    });
    setConnectionSucceeded(false); // Reset success flag
  }, []);

  // Track when edge update is in progress to prevent onConnectEnd from creating new nodes
  const edgeUpdateInProgressRef = useRef(false);
  
  const onEdgeUpdateStart = useCallback(() => {
    fcDebug('🔄 Edge update started');
    edgeUpdateInProgressRef.current = true;
  }, []);

  const handleConnect = useCallback((connection) => {
    const { source, target, sourceHandle, targetHandle } = connection;
        
    logFlowchartAction('Node connection initiated', {
      sourceNodeId: source,
      targetNodeId: target,
      sourceHandle: sourceHandle,
      targetHandle: targetHandle
    });
        
    if (!source || !target) {
      logFlowchartAction('Node connection failed - missing source or target', {
        hasSource: !!source,
        hasTarget: !!target
      });
      return;
    }
        
    // Prevent connections from end script nodes
    if (source === '__end_script__' || source === 'END_SCRIPT') {
      logFlowchartAction('Node connection blocked - cannot connect from END_SCRIPT', {
        sourceNodeId: source,
        targetNodeId: target
      });
      return;
    }
        
    // Find an available response key
    const currentEdges = getEdges();
    const existingConnectionsFromSource = currentEdges.filter(e => e.source === source);
    let responseKey = `response_${existingConnectionsFromSource.length + 1}`;
    let i = 1;
    while (currentEdges.find(e => e.data?.responseKey === responseKey && e.source === source)) {
      responseKey = `response_${++i}`;
    }
        
    // Count existing parallel edges for this pair
    const existingParallel = currentEdges.filter(e => e.source === source && e.target === target).length;

    const newEdge = {
      id: `${source}-${target}-${responseKey}`,
      source,
      target,
      sourceHandle: 'out-bottom',
      targetHandle: 'in-top',
      type: 'response',
      animated: false,
      markerEnd: { type: MarkerType.ArrowClosed },
      data: { responseKey, label: responseKey, parallelIndex: existingParallel },
      style: { strokeWidth: 2, stroke: '#6b7280' },
      zIndex: 0,
    };
        
    fcDebug('➕ Adding edge:', newEdge.id);
        
    // Update ReactFlow edges state
    const updatedEdges = [...currentEdges, newEdge];
    setEdges(updatedEdges);
        
    // Sync to parent IMMEDIATELY (not debounced) so node editor sees new responses
    const updatedNodes = getNodes();
    notifyGraphChange(updatedNodes, updatedEdges, true);
        
    logFlowchartAction('Node connection created', {
      sourceNodeId: source,
      targetNodeId: target,
      responseKey: responseKey,
      totalConnectionsFromSource: updatedEdges.filter(e => e.source === source).length
    });
        
    // Mark connection as successful and clear connection state
    setConnectionSucceeded(true);
    setConnectionState(null);
  }, [getNodes, getEdges, setEdges, notifyGraphChange]);

  const handleEdgeUpdate = useCallback((oldEdge, newConnection) => {
    fcDebug('🔄 Edge update:', oldEdge?.id);
    
    const { source: newSource, target: newTarget, sourceHandle, targetHandle } = newConnection;
    
    if (!newSource || !newTarget) {
      fcDebug('⚠️ Edge update cancelled - missing source or target');
      return;
    }
    
    // Update ReactFlow edges directly (source of truth)
    setEdges((currentEdges) => {
      const updatedEdges = currentEdges.map((edge) => {
        if (edge.id === oldEdge.id) {
          // Update this edge with new connection
          return {
            ...edge,
            source: newSource,
            target: newTarget,
            sourceHandle: 'out-bottom',
            targetHandle: 'in-top',
          };
        }
        return edge;
      });
      
      // Notify parent IMMEDIATELY
      const updatedNodes = getNodes();
      notifyGraphChange(updatedNodes, updatedEdges, true);
      
      return updatedEdges;
    });
    
    fcDebug('✅ Edge updated successfully');
  }, [setEdges, getNodes, notifyGraphChange]);

  const handlePaneDoubleClick = useCallback((event) => {
    // Only create node on double-click, not single click
    if (event.detail === 2) {
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      const currentNodes = getNodes();
      let newNodeId = `newNode_${currentNodes.length + 1}`;
      let i = 1;
      while (currentNodes.find(n => n.id === newNodeId)) {
        newNodeId = `newNode_${++i}`;
      }
            
      logFlowchartAction('New node created', {
        nodeId: newNodeId,
        position: position,
        totalNodesAfter: currentNodes.length + 1,
        creationMethod: 'double-click'
      });
            
      // Create new node in ReactFlow format directly
      const newNode = {
        id: newNodeId,
        type: 'question',
        position,
        data: {
          script: 'New question...',
          responses: {},
          step: 'interest',
          id: newNodeId,
          title: newNodeId,
          snippet: 'New question...',
          onEdit: () => handleInlineEdit(newNodeId),
          onDelete: () => handleDeleteNode(newNodeId)
        }
      };
            
      // New node added — layout is no longer purely auto
      if (isAutoLayoutRef.current) {
        setIsAutoLayout(false);
        isAutoLayoutRef.current = false;
      }

      // Update ReactFlow state and notify parent with the UPDATED nodes array
      setNodes(nds => {
        const updatedNodes = [...nds, newNode];
        // Debounced sync to parent with the new nodes array (500ms delay)
        notifyGraphChange(updatedNodes, edges, false);
        return updatedNodes;
      });
    }
  }, [screenToFlowPosition, getNodes, handleInlineEdit, handleDeleteNode, setNodes, setEdges, notifyGraphChange]);

  const onConnectEnd = useCallback((event) => {
    // Don't create new node if we're updating an existing edge
    if (edgeUpdateInProgressRef.current) {
      fcDebug('🔄 Edge update in progress, skipping node creation');
      setConnectionState(null);
      return;
    }
    
    // Check if we're dropping on a node — auto-connect to that node's in-top handle
    const nodeElement = event.target.closest('.react-flow__node');
    if (nodeElement) {
      const targetNodeId = nodeElement.getAttribute('data-id');
      if (targetNodeId && connectionState?.source && targetNodeId !== connectionState.source) {
        // Create edge to the dropped-on node
        handleConnect({
          source: connectionState.source,
          target: targetNodeId,
          sourceHandle: 'out-bottom',
          targetHandle: 'in-top',
        });
      }
      setConnectionState(null);
      setConnectionSucceeded(false);
      return;
    }

    // Check if we're dropping on the pane (empty space)
    const isPane = event.target.classList.contains('react-flow__pane') ||
                      event.target.classList.contains('react-flow__renderer') ||
                      event.target.closest('.react-flow__pane');

    if (!isPane) {
      setConnectionState(null); // Clear connection state
      return;
    }

    // If a connection was already successful (node-to-node), don't create a new node
    if (connectionSucceeded) {
      setConnectionState(null);
      setConnectionSucceeded(false);
      return;
    }

    if (!connectionState || !connectionState.source) {
      setConnectionState(null); // Clear connection state
      return;
    }

    // Prevent connections from end script nodes
    if (connectionState.source === '__end_script__') {
      setConnectionState(null); // Clear connection state
      return;
    }
        
    const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
    const currentNodes = getNodes();
    let newNodeId = `newNode_${currentNodes.length + 1}`;
    let i = 1;
    while (currentNodes.find(n => n.id === newNodeId)) {
      newNodeId = `newNode_${++i}`;
    }
        
    // Determine response key for the connection
    let responseKey = 'response_1';
    let j = 1;
    const sourceNode = currentNodes.find(n => n.id === connectionState.source);
    if (sourceNode?.data?.responses) {
      while (sourceNode.data.responses[`response_${j}`]) {
        j++;
      }
      responseKey = `response_${j}`;
    }
        
    // Create new node in ReactFlow format
    const newNode = {
      id: newNodeId,
      type: 'question',
      position,
      data: {
        script: 'New Question',
        responses: {},
        step: 'interest',
        id: newNodeId,
        title: newNodeId,
        snippet: 'New Question',
        onEdit: () => handleInlineEdit(newNodeId),
        onDelete: () => handleDeleteNode(newNodeId)
      }
    };
        
    // Create edge in ReactFlow format with consistent styling
    const newEdge = {
      id: `${connectionState.source}-${responseKey}`,
      source: connectionState.source,
      target: newNodeId,
      sourceHandle: connectionState.sourceHandle || 'out-bottom', 
      targetHandle: 'in-top',
      type: 'response',
      animated: false,
      data: { responseKey, label: responseKey, parallelIndex: 0 },
      markerEnd: { type: MarkerType.ArrowClosed },
      style: { strokeWidth: 2, stroke: '#6b7280' },
      zIndex: 0,
    };
        
    // Update ReactFlow state and notify parent with the UPDATED arrays
    // New node added via connection drop — layout is no longer purely auto
    if (isAutoLayoutRef.current) {
      setIsAutoLayout(false);
      isAutoLayoutRef.current = false;
    }

    const updatedNodes = [...getNodes(), newNode];
    const updatedEdges = [...edges, newEdge];
    setNodes(updatedNodes);
    setEdges(updatedEdges);
        
    // Sync to parent IMMEDIATELY so node editor sees new node + edge
    notifyGraphChange(updatedNodes, updatedEdges, true);
        
    // Clear connection state after successful creation
    setConnectionState(null);
    setConnectionSucceeded(false);
  }, [screenToFlowPosition, getNodes, edges, handleInlineEdit, handleDeleteNode, setNodes, setEdges, notifyGraphChange, connectionState, connectionSucceeded, handleConnect]);
    
  // Note: handleSaveAndExit removed - saving now handled by useImperativeHandle

  const onEdgeUpdateEnd = useCallback(() => {
    fcDebug('🔄 Edge update ended');
    edgeUpdateInProgressRef.current = false;
  }, []);

  const onEdgeDoubleClick = useCallback(async (_, edge) => {
    const currentLabel = edge?.data?.label || edge?.id || '';
    const newLabel = await promptDialog('Edit response label', currentLabel);
    if (newLabel == null) return; // cancelled
        
    setEdges((eds) => {
      const updatedEdges = eds.map((e) => (
        e.id === edge.id ? { ...e, data: { ...(e.data || {}), label: newLabel } } : e
      ));
      const updatedNodes = getNodes();
      notifyGraphChange(updatedNodes, updatedEdges, false);
      return updatedEdges;
    });
  }, [getNodes, notifyGraphChange, setEdges]);

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }} ref={containerRef}>
      {/* Center View Button */}
      <button
        onClick={handleCenterView}
        style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          zIndex: 5,
          padding: '8px 12px',
          background: 'white',
          border: '1px solid #ddd',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '500',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}
        title="Go to start of script"
        onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
        onMouseOut={(e) => e.target.style.background = 'white'}
      >
                🏁 Go to Start
      </button>
            
      {/* Go to End Button */}
      <button
        onClick={() => {
          fcDebug('🎯 Manual Go to End triggered');
          const allNodes = getNodes();
          const endNode = allNodes.find(n => n.id === '__end_script__');
                    
          if (endNode && endNode.position) {
            const centerX = endNode.position.x + 175;
            const centerY = endNode.position.y + 105;
            fcDebug('🎯 Centering on end node:', endNode.id);
            setCenter(centerX, centerY, { zoom: 0.6, duration: 500 });
          } else {
            fcDebug('🎯 No end node found, using fitView');
            fitView({ padding: 0.1, duration: 500 });
          }
        }}
        style={{
          position: 'absolute',
          top: '50px',
          left: '10px',
          zIndex: 5,
          padding: '8px 12px',
          background: 'white',
          border: '1px solid #ddd',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '500',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}
        title="Go to end of script"
        onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
        onMouseOut={(e) => e.target.style.background = 'white'}
      >
                🏁 Go to End
      </button>
            
      <div style={{ position: 'absolute', zIndex: 10, right: 10, top: 10, display: 'flex', gap: '8px' }}>
        {/* Help Button */}
        <button
          onClick={() => setShowHelpModal(true)}
          style={{
            padding: '8px 12px',
            background: 'white',
            color: '#6b7280',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
          title="How to use the Flowchart Editor"
          onMouseOver={(e) => { e.target.style.background = '#f3f4f6'; e.target.style.color = '#374151'; }}
          onMouseOut={(e) => { e.target.style.background = 'white'; e.target.style.color = '#6b7280'; }}
        >
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Help
        </button>
        {/* Auto Layout Button — greyed out when auto-layout is already active */}
        <button 
          disabled={isAutoLayout || isLayoutRunning}
          onClick={async () => {
            if (isAutoLayout || isLayoutRunning) return;
            if (!graph?.nodes || Object.keys(graph.nodes).length === 0) return;
            
            setIsLayoutRunning(true);
                        
            // Yield to browser so the loading state renders before ELK blocks the thread
            await new Promise(resolve => requestAnimationFrame(() => setTimeout(resolve, 50)));
            
            try {
              const { positions } = await layoutGraph(graph);
              
              if (Object.keys(positions).length === 0) {
                setIsLayoutRunning(false);
                return;
              }
                          
              const currentNodes = getNodes();
              const updatedNodes = currentNodes.map(node => ({
                ...node,
                position: positions[node.id] || node.position
              }));
              setNodes(updatedNodes);
              
              const currentEdges = getEdges();
              const updatedEdges = currentEdges.map(edge => ({
                ...edge,
                type: edge.type === 'response' ? 'response' : 'smoothstep',
                sourceHandle: 'out-bottom',
                targetHandle: 'in-top',
                style: { strokeWidth: 2, stroke: '#6b7280' },
                markerEnd: edge.markerEnd || { type: 'arrowclosed' },
                zIndex: 0,
              }));
              setEdges(updatedEdges);

              setIsAutoLayout(true);
              isAutoLayoutRef.current = true;
              notifyGraphChange(updatedNodes, updatedEdges, false);
                          
              setTimeout(() => {
                try {
                  const openingNode = updatedNodes.find(n => n.data?.isOpeningNode);
                  if (openingNode && openingNode.position) {
                    setCenter(openingNode.position.x + 175, openingNode.position.y + 105, { zoom: 0.6, duration: 500 });
                  } else {
                    fitView({ padding: 0.15, duration: 500 });
                  }
                } catch (error) {
                  if (!error.message?.includes?.('ResizeObserver')) {
                    console.error('Auto Layout centering error:', error);
                  }
                }
              }, 200);
            } catch (error) {
              console.error('Auto Layout error:', error);
            } finally {
              setIsLayoutRunning(false);
            }
          }} 
          style={{ 
            padding: '8px 12px', 
            background: isLayoutRunning ? '#fbbf24' : isAutoLayout ? '#93c5fd' : '#3b82f6', 
            color: isLayoutRunning ? '#92400e' : 'white',
            border: 'none', 
            borderRadius: '6px', 
            cursor: (isAutoLayout || isLayoutRunning) ? 'default' : 'pointer',
            fontWeight: '500',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            opacity: (isAutoLayout || isLayoutRunning) ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
          title={isLayoutRunning ? 'Computing layout...' : isAutoLayout ? 'Auto layout is already applied' : 'Re-arrange all nodes automatically'}
          onMouseOver={(e) => { if (!isAutoLayout && !isLayoutRunning) e.target.style.background = '#2563eb'; }}
          onMouseOut={(e) => { e.target.style.background = isLayoutRunning ? '#fbbf24' : isAutoLayout ? '#93c5fd' : '#3b82f6'; }}
        >
          {isLayoutRunning ? (
            <svg width="14" height="14" viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="31.4 31.4" strokeLinecap="round" />
            </svg>
          ) : isAutoLayout ? (
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          ) : null}
          {isLayoutRunning ? 'Computing...' : 'Auto Layout'}
        </button>
      </div>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onInit={handleReactFlowInit}
        onNodesChange={(changes) => { persistViewport(); handleNodesChange(changes); }}
        onEdgesChange={(changes) => { persistViewport(); handleEdgesChange(changes); }}
        onNodeDragStop={() => {
          // User manually dragged a node — layout is no longer auto
          if (isAutoLayoutRef.current) {
            fcDebug('📐 Manual node drag detected — switching to manual layout');
            setIsAutoLayout(false);
            isAutoLayoutRef.current = false;
          }
          // Persist position changes to parent
          notifyGraphChange(getNodes(), getEdges(), false);
        }}
        onMoveEnd={(_, viewport) => persistViewport(viewport)}
        onConnect={handleConnect}
        onConnectStart={onConnectStart}
        onConnectEnd={onConnectEnd}
        onEdgeUpdateStart={onEdgeUpdateStart}
        onEdgeUpdate={handleEdgeUpdate}
        onEdgeUpdateEnd={onEdgeUpdateEnd}
        onPaneClick={handlePaneDoubleClick}
        onEdgeDoubleClick={onEdgeDoubleClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        minZoom={0.1}
        maxZoom={1.2}
        connectionLineType="smoothstep"
        elevateEdgesOnSelect={false}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={true}
        nodesConnectable={true}
        elementsSelectable={true}
        selectionOnDrag={false}
        selectionMode={'partial'}
        selectionKeyCode="Shift"
        panOnDrag={true}
        panOnScroll={true}
        zoomOnScroll={false}
        zoomOnPinch={true}
        connectionMode="loose"
        edgesUpdatable={true}
        snapToGrid={false}
        snapGrid={[15, 15]}
      >
        <Controls />
        <Background gap={20} size={1} color="#e5e7eb" />
      </ReactFlow>

      {/* Empty state overlay — shown when no user-created nodes exist */}
      {nodes.filter(n => n.id !== '__end_script__').length === 0 && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none', zIndex: 4
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.92)', border: '2px dashed #CBD5E1',
            borderRadius: '12px', padding: '24px 36px', textAlign: 'center',
            maxWidth: '340px'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>✏️</div>
            <div style={{ fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
              Start building your script
            </div>
            <div style={{ color: '#6B7280', fontSize: '14px' }}>
              Double-click anywhere on the canvas to add your first question node
            </div>
          </div>
        </div>
      )}

      {/* Inline Edit Modal */}
      {showEditModal && (
        <Modal
          isOpen={showEditModal}
          onClose={handleCancelInlineEdit}
          title={editingNodeData.isEndNode ? 'Edit End Script' : 'Edit Question Node'}
          size="lg"
        >
                        
            {!editingNodeData.isEndNode && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  color: '#374151'
                }}>
                                    Question ID
                </label>
                <input
                  type="text"
                  value={editingNodeData.id}
                  onChange={(e) => setEditingNodeData(prev => ({ ...prev, id: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="e.g., initial_greeting, price_objection"
                />
              </div>
            )}
                        
            {!editingNodeData.isEndNode && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '6px', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  color: '#374151'
                }}>
                                    Script Phase
                </label>
                <select
                  value={editingNodeData.step}
                  onChange={(e) => setEditingNodeData(prev => ({ ...prev, step: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                >
                  {templateSteps && templateSteps.length > 0 ? (
                    templateSteps.map(step => (
                      <option key={step.id} value={step.id}>
                        {step.name} - {step.description || step.name}
                      </option>
                    ))
                  ) : (
                    // Fallback to defaults if no template steps
                    <>
                      <option value="interest">Interest - Ensuring there is interest to sell</option>
                      <option value="motivation">Motivation - Determining motivations to sell</option>
                      <option value="condition">Condition - Learning about house condition and call to action</option>
                    </>
                  )}
                </select>
              </div>
            )}
            
            {/* Opening Node Toggle */}
            {!editingNodeData.isEndNode && (
              <div style={{ 
                marginBottom: '16px',
                padding: '12px',
                background: editingNodeData.isOpeningNode ? '#ecfdf5' : '#f9fafb',
                borderRadius: '8px',
                border: editingNodeData.isOpeningNode ? '1px solid #10b981' : '1px solid #e5e7eb'
              }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: editingNodeData.isOpeningNode ? '#059669' : '#374151'
                }}>
                  <input
                    type="checkbox"
                    checked={editingNodeData.isOpeningNode || false}
                    onChange={(e) => setEditingNodeData(prev => ({ ...prev, isOpeningNode: e.target.checked }))}
                    style={{
                      width: '18px',
                      height: '18px',
                      accentColor: '#10b981',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '16px' }}>▶</span>
                    This is the Starting Node
                  </span>
                </label>
                <p style={{ 
                  margin: '8px 0 0 28px', 
                  fontSize: '12px', 
                  color: '#6b7280',
                  lineHeight: '1.4'
                }}>
                  The call will begin at this node. Only one node can be the starting node.
                </p>
              </div>
            )}
                        
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px', 
                fontSize: '14px', 
                fontWeight: '500',
                color: '#374151'
              }}>
                {editingNodeData.isEndNode ? 'Closing Statement' : 'What You\'ll Say'}
              </label>
              <textarea
                value={editingNodeData.script}
                onChange={(e) => setEditingNodeData(prev => ({ ...prev, script: e.target.value }))}
                rows={4}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit'
                }}
                placeholder="What will you say to the prospect at this point?"
              />
            </div>
                        
            <div style={{ marginBottom: '24px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '6px', 
                fontSize: '14px', 
                fontWeight: '500',
                color: '#374151'
              }}>
                {editingNodeData.isEndNode ? 'Transition Notes' : 'Tips & Coaching Notes'}
              </label>
              <textarea
                value={editingNodeData.coaching}
                onChange={(e) => setEditingNodeData(prev => ({ ...prev, coaching: e.target.value }))}
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit'
                }}
                placeholder="Tips for handling this part of the conversation..."
              />
            </div>
                        
            {/* Responses Section (toggled by Advanced Settings) */}
            {!editingNodeData.isEndNode && showAdvanced && (() => {
              const nodeResponses = edges.filter(e => e.source === editingNodeId);
              const flowTargetOptions = nodes
                .filter(n => n.id !== '__end_script__' && n.id !== editingNodeId)
                .map(n => ({
                  id: n.id,
                  label: n.data?.script ? `${n.id} - ${(n.data.script || '').substring(0, 30)}` : n.id,
                }));
              flowTargetOptions.push({ id: '__end_script__', label: 'End Script' });

              const normalizedResponses = nodeResponses.map(edge => ({
                id: edge.id,
                label: edge.data?.label || '',
                type: edge.data?.responseType || 'predetermined',
                target: edge.target,
              }));

              return (
                <div style={{ marginBottom: '16px' }}>
                  <NodeResponsesTable
                    responses={normalizedResponses}
                    targetOptions={flowTargetOptions}
                    emptyMessage="No responses yet. Add one or drag a connection from this node in the flowchart."
                    onUpdateLabel={(id, newLabel) => {
                      setEdges(eds => eds.map(ed => ed.id === id ? { ...ed, data: { ...ed.data, label: newLabel } } : ed));
                    }}
                    onUpdateType={(id, newType) => {
                      setEdges(eds => eds.map(ed => ed.id === id ? { ...ed, data: { ...ed.data, responseType: newType } } : ed));
                    }}
                    onUpdateTarget={(id, newTarget) => {
                      setEdges(eds => eds.map(ed => ed.id === id ? { ...ed, target: newTarget, id: `${ed.source}-${newTarget}-${ed.data?.responseKey}` } : ed));
                    }}
                    onRemove={(id) => {
                      setEdges(eds => eds.filter(ed => ed.id !== id));
                    }}
                    onAdd={() => {
                      setEdges(eds => {
                        const existingFromNode = eds.filter(e => e.source === editingNodeId);
                        let responseKey = `response_${existingFromNode.length + 1}`;
                        let idx = existingFromNode.length + 1;
                        while (eds.find(e => e.data?.responseKey === responseKey && e.source === editingNodeId)) {
                          responseKey = `response_${++idx}`;
                        }
                        const firstTarget = flowTargetOptions[0]?.id || '__end_script__';
                        const newEdge = {
                          id: `${editingNodeId}-${firstTarget}-${responseKey}`,
                          source: editingNodeId,
                          target: firstTarget,
                          sourceHandle: 'out-bottom',
                          targetHandle: 'in-top',
                          type: 'response',
                          animated: false,
                          markerEnd: { type: MarkerType.ArrowClosed },
                          data: { responseKey, label: responseKey, parallelIndex: 0, responseType: 'predetermined' },
                          style: { strokeWidth: 2, stroke: '#6b7280' },
                          zIndex: 0,
                        };
                        return [...eds, newEdge];
                      });
                    }}
                  />
                </div>
              );
            })()}

            {/* Modal Footer */}
            <Modal.Footer>
              <Button
                variant="outline"
                size="md"
                onClick={handleCancelInlineEdit}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleSaveInlineEdit}
              >
                Save Changes
              </Button>
            </Modal.Footer>
        </Modal>
      )}

      {/* Help Modal */}
      {showHelpModal && (
        <Modal
          isOpen={showHelpModal}
          onClose={() => {
            setShowHelpModal(false);
            localStorage.setItem('flowchartHelpDismissed', 'true');
          }}
          title="How to Use the Flowchart Editor"
          size="lg"
        >
            <div style={{ fontSize: '14px', color: '#374151' }}>
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                <p style={{ margin: 0, color: '#1e40af', fontWeight: 500 }}>💡 Think of this like building a conversation map for your phone calls. Each box represents something you'll say, and the lines show how prospects might respond.</p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: '#1f2937' }}>
                  <span style={{ background: '#dcfce7', color: '#166534', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700 }}>1</span>
                  Basic Actions
                </h4>
                <ul style={{ marginLeft: '32px', listStyle: 'none', padding: 0 }}>
                  <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ color: '#2563eb' }}>✏️</span>
                    <div><strong>Edit what you'll say:</strong> Click the "Edit" button on any box to change your script</div>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ color: '#2563eb' }}>🖱️</span>
                    <div><strong>Move boxes around:</strong> Click and drag any box to reposition it</div>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ color: '#2563eb' }}>🔗</span>
                    <div><strong>Connect responses:</strong> Drag from the small circles on a box to another box</div>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ color: '#dc2626' }}>🗑️</span>
                    <div><strong>Delete boxes:</strong> Hover over any box and click the red trash icon</div>
                  </li>
                </ul>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: '#1f2937' }}>
                  <span style={{ background: '#dcfce7', color: '#166534', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700 }}>2</span>
                  Building Your Call Flow
                </h4>
                <ul style={{ marginLeft: '32px', listStyle: 'none', padding: 0 }}>
                  <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ color: '#16a34a' }}>➕</span>
                    <div><strong>Add new questions:</strong> Double-click on any empty space to create a new step</div>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ color: '#9333ea' }}>🏷️</span>
                    <div><strong>Name your responses:</strong> Double-click on any line to edit the response label</div>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ color: '#ca8a04' }}>🎯</span>
                    <div><strong>End the conversation:</strong> Connect final responses to the yellow "End Script" box</div>
                  </li>
                </ul>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: '#1f2937' }}>
                  <span style={{ background: '#dcfce7', color: '#166534', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700 }}>3</span>
                  Navigation & Tools
                </h4>
                <ul style={{ marginLeft: '32px', listStyle: 'none', padding: 0 }}>
                  <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ color: '#2563eb' }}>✨</span>
                    <div><strong>Auto Layout:</strong> Click the blue button to automatically organize your flowchart</div>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ color: '#6b7280' }}>🔍</span>
                    <div><strong>Zoom:</strong> Use mouse wheel or trackpad to zoom in/out</div>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ color: '#6b7280' }}>👆</span>
                    <div><strong>Pan:</strong> Click and drag on empty areas to move around</div>
                  </li>
                </ul>
              </div>

              <div style={{ background: '#fefce8', border: '1px solid #fde047', borderRadius: '8px', padding: '16px' }}>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', color: '#854d0e' }}>
                  <span>🌟</span> Pro Tips
                </h4>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', color: '#854d0e' }}>
                  <li style={{ marginBottom: '6px' }}>• Use <strong>Advanced Settings</strong> in the edit modal for objections & response types</li>
                  <li style={{ marginBottom: '6px' }}>• Click <strong>Go to Start</strong> or <strong>Go to End</strong> to quickly navigate</li>
                  <li style={{ marginBottom: '6px' }}>• Save your work manually using the Save button in the header</li>
                  <li>• Use <strong>Auto Layout</strong> whenever things get messy</li>
                </ul>
              </div>
            </div>
        </Modal>
      )}

      {/* Delete Node Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteNodeConfirm}
        onClose={() => { setShowDeleteNodeConfirm(false); setPendingDeleteNodeId(null); }}
        onConfirm={confirmDeleteNode}
        title="Delete Node?"
        message={`Delete node "${pendingDeleteNodeId}"? This also removes connected responses.`}
        confirmLabel="Delete"
        confirmVariant="danger"
      />

      {/* Discard Changes Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDiscardChangesConfirm}
        onClose={() => setShowDiscardChangesConfirm(false)}
        onConfirm={confirmDiscardChanges}
        title="Discard Changes?"
        message="You have unsaved changes. Discard changes and close?"
        confirmLabel="Discard"
        confirmVariant="danger"
      />

      {/* Node ID Rename Warning */}
      <ConfirmDialog
        isOpen={showRenameConfirm}
        onClose={() => { setShowRenameConfirm(false); pendingRenameResolve?.(false); setPendingRenameResolve(null); }}
        onConfirm={() => { setShowRenameConfirm(false); pendingRenameResolve?.(true); setPendingRenameResolve(null); }}
        title="Renaming this node will affect analytics"
        message={
          <span style={{ textAlign: 'left', display: 'block' }}>
            <span style={{ display: 'block', marginBottom: 10 }}>
              This node ID is used to track performance in Command Center. Renaming it will break the connection to historical data:
            </span>
            <span style={{ display: 'block', marginBottom: 4 }}>• <strong>Drop-off Analysis</strong> — historical counts for this node will no longer appear</span>
            <span style={{ display: 'block', marginBottom: 4 }}>• <strong>Script Health</strong> — completion rates tied to this node may shift</span>
            <span style={{ display: 'block', marginBottom: 10 }}>• <strong>AI Improve</strong> — improvement suggestions based on this node's history will be lost</span>
            <span style={{ display: 'block', color: '#6B7280', fontSize: '0.875rem' }}>
              To change what the agent says without affecting analytics, edit the script content instead.
            </span>
          </span>
        }
        confirmLabel="Rename anyway"
        confirmVariant="danger"
      />
    </div>
  );
}

// Error boundary component to catch React Flow errors
class FlowChartErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    // Check if it's a ResizeObserver error and ignore it
    if (error?.message?.includes('ResizeObserver')) {
      return { hasError: false };
    }
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log non-ResizeObserver errors
    if (!error?.message?.includes('ResizeObserver')) {
      console.error('🎯 FlowChart Error Boundary caught error:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center', 
          background: '#fef2f2', 
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#dc2626'
        }}>
          <h3>Flowchart Editor Error</h3>
          <p>Something went wrong with the flowchart editor. Please refresh the page.</p>
        </div>
      );
    }

    return this.props.children;
  }
}

const EditorImplWithRef = React.forwardRef(EditorImpl);

// Memoize to prevent re-renders when props haven't meaningfully changed
const MemoizedEditorImpl = React.memo(EditorImplWithRef, (prevProps, nextProps) => {
  // Custom comparison: only re-render if graph structure actually changes
  const prevNodeCount = Object.keys(prevProps.graph?.nodes || {}).length;
  const nextNodeCount = Object.keys(nextProps.graph?.nodes || {}).length;
    
  if (prevNodeCount !== nextNodeCount) {
    return false; // Re-render
  }
    
  // Check if node IDs changed
  const prevNodeIds = Object.keys(prevProps.graph?.nodes || {}).sort().join(',');
  const nextNodeIds = Object.keys(nextProps.graph?.nodes || {}).sort().join(',');
    
  if (prevNodeIds !== nextNodeIds) {
    return false; // Re-render
  }
  
  // Check if steps changed (for the phase dropdown)
  const prevStepsIds = (prevProps.steps || []).map(s => s.id).join(',');
  const nextStepsIds = (nextProps.steps || []).map(s => s.id).join(',');
  if (prevStepsIds !== nextStepsIds) {
    return false; // Re-render
  }
    
  // onChange function reference might change, but we don't care
  // onEditNode function reference might change, but we don't care
  // persistPositions function reference might change, but we don't care
    
  fcDebug('✅ Props unchanged - skipping re-render');
  return true; // Skip re-render
});

export default React.forwardRef(function FlowChartEditor(props, ref) {
  return (
    <FlowChartErrorBoundary>
      <ReactFlowProvider>
        <MemoizedEditorImpl {...props} ref={ref} />
      </ReactFlowProvider>
    </FlowChartErrorBoundary>
  );
});
